// ═══════════════════════════════════════════════════════════════
// GAB Admin API — roles, users, permissions
// All calls go through the same v2 proxy used by the data layer.
// Auth is cookie-based — credentials: 'include' is all we need.
// ═══════════════════════════════════════════════════════════════

import { getToken } from '../auth/token';

const BASE  = '/api/v2proxy';
const APP = import.meta.env.VITE_V2_APP_ID || '8q39q8q5n';

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const oidcToken = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(oidcToken ? { 'Authorization': `Bearer ${oidcToken}` } : {}),
  };
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers,
    ...opts,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${path}: ${text}`);
  }
  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

// ── Types ────────────────────────────────────────────────────────

export interface GabRole {
  id: string;
  name: string;
  description?: string;
  isSystem?: boolean;
  createdAt?: string;
}

export interface GabUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  role?: string;  // platform role (super_admin etc.)
}

export interface TablePermission {
  id: string;        // permission entry UUID
  tableId?: string;  // table UUID (returned by role-specific endpoint)
  key?: string;      // table base36 key (returned by my-permissions endpoint)
  name?: string;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  viewAccess: 'none' | 'all' | 'custom';
  editAccess: 'none' | 'all' | 'custom';
  deleteAccess: 'none' | 'all' | 'custom';
}

export interface MyPermissionsResponse {
  tables: TablePermission[];
}

export interface GabTable {
  id: string;
  key: string;
  name: string;
  slug: string;
}

// ── My Permissions (current user) ────────────────────────────────

export async function getMyPermissions(): Promise<MyPermissionsResponse> {
  return req<MyPermissionsResponse>(`/v2/apps/${APP}/my-permissions`);
}

// ── Roles ────────────────────────────────────────────────────────

export async function listRoles(): Promise<GabRole[]> {
  const res = await req<{ items?: GabRole[]; roles?: GabRole[] } | GabRole[]>(`/v2/apps/${APP}/roles`);
  if (Array.isArray(res)) return res;
  return (res as any).items ?? (res as any).roles ?? [];
}

export async function createRole(name: string, description?: string): Promise<GabRole> {
  return req<GabRole>(`/v2/apps/${APP}/roles`, {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  });
}

export async function updateRole(roleId: string, name: string, description?: string): Promise<GabRole> {
  return req<GabRole>(`/v2/apps/${APP}/roles/${roleId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name, description }),
  });
}

export async function deleteRole(roleId: string): Promise<void> {
  return req<void>(`/v2/apps/${APP}/roles/${roleId}`, { method: 'DELETE' });
}

// ── Role Table Permissions ────────────────────────────────────────

export async function getRoleTablePermissions(roleId: string): Promise<TablePermission[]> {
  const res = await req<{ items?: TablePermission[]; tables?: TablePermission[] } | TablePermission[]>(
    `/v2/apps/${APP}/roles/${roleId}/tables`
  );
  if (Array.isArray(res)) return res;
  return (res as any).items ?? (res as any).tables ?? [];
}

export interface TablePermissionPatch {
  viewAccess?: 'none' | 'all' | 'custom';
  canAdd?: boolean;
  editAccess?: 'none' | 'all' | 'custom';
  deleteAccess?: 'none' | 'all';
}

export async function setTablePermission(
  roleId: string,
  tableId: string,
  perms: TablePermissionPatch,
): Promise<void> {
  // Use raw fetch — PATCH returns 200 with empty body (not 204), so req() would
  // blow up trying to parse JSON. Just drain the body and check status.
  const res = await fetch(`${BASE}/v2/apps/${APP}/roles/${roleId}/tables/${tableId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: H,
    body: JSON.stringify(perms),
  });
  const text = await res.text().catch(() => '');
  if (!res.ok) throw new Error(`${res.status}: ${text}`);
}

// ── App Tables (for permission editor) ───────────────────────────

export async function listAppTables(): Promise<GabTable[]> {
  const res = await req<{ items?: GabTable[]; tables?: GabTable[] } | GabTable[]>(`/v2/apps/${APP}/tables`);
  if (Array.isArray(res)) return res;
  return (res as any).items ?? (res as any).tables ?? [];
}

// ── App Users ────────────────────────────────────────────────────

export async function listAppUsers(): Promise<GabUser[]> {
  const res = await req<{ items?: GabUser[]; users?: GabUser[] } | GabUser[]>(`/v2/apps/${APP}/users`);
  if (Array.isArray(res)) return res;
  return (res as any).items ?? (res as any).users ?? [];
}

export async function removeUserFromApp(userId: string): Promise<void> {
  return req<void>(`/v2/apps/${APP}/users/${userId}`, { method: 'DELETE' });
}

// ── User Role Assignments ─────────────────────────────────────────

export async function getUserRoles(userId: string): Promise<GabRole[]> {
  const res = await req<{ items?: GabRole[]; roles?: GabRole[] } | GabRole[]>(
    `/v2/apps/${APP}/users/${userId}/roles`
  );
  if (Array.isArray(res)) return res;
  return (res as any).items ?? (res as any).roles ?? [];
}

export async function assignRole(userId: string, roleId: string): Promise<void> {
  return req<void>(`/v2/apps/${APP}/users/${userId}/roles`, {
    method: 'POST',
    body: JSON.stringify({ roleId }),
  });
}

export async function removeRoleFromUser(userId: string, roleId: string): Promise<void> {
  return req<void>(`/v2/apps/${APP}/users/${userId}/roles/${roleId}`, { method: 'DELETE' });
}

// ── Invite User ───────────────────────────────────────────────────

export async function inviteUser(email: string, firstName?: string, lastName?: string): Promise<void> {
  return req<void>(`/v2/users/invite`, {
    method: 'POST',
    body: JSON.stringify({ email, firstName, lastName, appId: APP }),
  });
}
